import { reactive, readonly } from 'vue'
import type { PublicBranch, PublicReservationProduct, PublicEmployee } from '@/types/api'

export type WizardStep =
  | 'branches'
  | 'products'
  | 'datetime'
  | 'employees'
  | 'customer'
  | 'payment'
  | 'summary'

export const wizardState = reactive<{
  step: WizardStep
  facilityId: number | null
  branch: PublicBranch | null
  product: PublicReservationProduct | null
  selectedDate: string | null
  selectedTime: string | null
  selectedSlot: { start: string; end: string; employee_id?: number } | null
  employee: PublicEmployee | null
  customer: {
    name: string
    surname: string
    email: string
    phone: string
    note: string
    numberOfPeople: number
    couponCode: string
    couponPassword: string
  }
  zdDealItemId: string | null
  couponVerified: boolean | null
  couponVerificationError: string | null
  isCouponVerifying: boolean
  priceOverride: number | null
  isExternalPaid: boolean
  selectedPaymentMethodId: number | null
  termsAgreement: boolean
  privacyAgreement: boolean
  reservationId: number | null
  submitted: boolean
}>({
  step: 'branches',
  facilityId: null,
  branch: null,
  product: null,
  selectedDate: null,
  selectedTime: null,
  selectedSlot: null,
  employee: null,
  customer: {
    name: '',
    surname: '',
    email: '',
    phone: '',
    note: '',
    numberOfPeople: 1,
    couponCode: '',
    couponPassword: '',
  },
  zdDealItemId: null,
  couponVerified: null,
  couponVerificationError: null,
  isCouponVerifying: false,
  priceOverride: null,
  isExternalPaid: false,
  selectedPaymentMethodId: null,
  termsAgreement: false,
  privacyAgreement: false,
  reservationId: null,
  submitted: false,
})

export function useWizardState() {
  return readonly(wizardState)
}

export function setWizardStep(step: WizardStep) {
  wizardState.step = step
}

export function setBranch(branch: PublicBranch | null) {
  wizardState.branch = branch
  wizardState.couponVerified = null
  wizardState.couponVerificationError = null
  wizardState.isCouponVerifying = false
}

export function setProduct(product: PublicReservationProduct | null) {
  wizardState.product = product
  if (!product) return
  const isWorkshop = product.type === 'workshop' || (product as { is_workshop?: boolean }).is_workshop
  const workshopDatetime = (product as { workshop_datetime?: string }).workshop_datetime
  if (isWorkshop && workshopDatetime) {
    const dt = workshopDatetime.replace('Z', '').slice(0, 19)
    const [datePart, timePart] = dt.split('T')
    wizardState.selectedDate = datePart ?? null
    wizardState.selectedTime = timePart?.slice(0, 5) ?? null
    const duration = (product as { duration_minutes?: number; duration?: number }).duration_minutes
      ?? (product as { duration_minutes?: number; duration?: number }).duration ?? 60
    const startDate = new Date(dt)
    const endDate = new Date(startDate.getTime() + duration * 60 * 1000)
    const pad = (n: number) => String(n).padStart(2, '0')
    const endStr = `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}T${pad(endDate.getHours())}:${pad(endDate.getMinutes())}:${pad(endDate.getSeconds())}`
    wizardState.selectedSlot = { start: dt, end: endStr }
    wizardState.employee = null
  } else if (isWorkshop) {
    wizardState.employee = null
  }
}

export function setSelectedDateTime(
  date: string | null,
  time: string | null,
  slot: { start: string; end: string; employee_id?: number } | null
) {
  wizardState.selectedDate = date
  wizardState.selectedTime = time
  wizardState.selectedSlot = slot
  if (slot?.employee_id) {
    wizardState.employee = { id: slot.employee_id, name: '' }
  }
}

export function setEmployee(employee: PublicEmployee | null) {
  wizardState.employee = employee
}

export function setCustomer(customer: Partial<typeof wizardState.customer>) {
  Object.assign(wizardState.customer, customer)
}

export function setSelectedPaymentMethodId(id: number | null) {
  wizardState.selectedPaymentMethodId = id
}

export function setZdDealItemId(value: string | null) {
  if (wizardState.zdDealItemId === value) return
  wizardState.zdDealItemId = value
  wizardState.couponVerified = null
  wizardState.couponVerificationError = null
  wizardState.isCouponVerifying = false
}

export function setCouponVerification(
  verified: boolean | null,
  error: string | null,
  verifying: boolean
) {
  wizardState.couponVerified = verified
  wizardState.couponVerificationError = error
  wizardState.isCouponVerifying = verifying
}

export function setExternalPricing(price: number | null, paid: boolean) {
  wizardState.priceOverride = price
  wizardState.isExternalPaid = paid
}

export function setTermsAgreement(value: boolean) {
  wizardState.termsAgreement = value
}

export function setPrivacyAgreement(value: boolean) {
  wizardState.privacyAgreement = value
}

export function setReservationId(id: number | null) {
  wizardState.reservationId = id
}

export function setSubmitted(value: boolean) {
  wizardState.submitted = value
}

export function resetWizard() {
  wizardState.step = 'branches'
  wizardState.branch = null
  wizardState.product = null
  wizardState.selectedDate = null
  wizardState.selectedTime = null
  wizardState.selectedSlot = null
  wizardState.employee = null
  wizardState.customer = {
    name: '',
    surname: '',
    email: '',
    phone: '',
    note: '',
    numberOfPeople: 1,
    couponCode: '',
    couponPassword: '',
  }
  wizardState.zdDealItemId = null
  wizardState.couponVerified = null
  wizardState.couponVerificationError = null
  wizardState.isCouponVerifying = false
  wizardState.priceOverride = null
  wizardState.isExternalPaid = false
  wizardState.selectedPaymentMethodId = null
  wizardState.termsAgreement = false
  wizardState.privacyAgreement = false
  wizardState.reservationId = null
  wizardState.submitted = false
}
